import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, Check, AlertCircle, ChevronRight, ArrowLeft } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';

export default function ImportCSV() {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    
    const [step, setStep] = useState('upload'); // 'upload', 'mapping', 'preview', 'complete'
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const [mapping, setMapping] = useState({});
    const [delimiter, setDelimiter] = useState(',');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [importResult, setImportResult] = useState(null);
    
    // Campi disponibili per il mapping
    const availableFields = [
        { value: 'nome', label: 'Nome' },
        { value: 'cognome', label: 'Cognome' },
        { value: 'email', label: 'Email' },
        { value: 'telefono', label: 'Telefono' },
        { value: 'indirizzo', label: 'Indirizzo' },
        { value: 'citta', label: 'Città' },
        { value: 'cap', label: 'CAP' },
        { value: 'data_nascita', label: 'Data di Nascita' },
        { value: 'note', label: 'Note' }
    ];
    
    // Handle file upload
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (!file.name.endsWith('.csv')) {
            setError('File deve essere un CSV');
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            // Leggi file con FileReader
            const reader = new FileReader();
            reader.onload = (event) => {
                const text = event.target.result;
                parseCSV(text);
            };
            reader.onerror = () => {
                setError('Errore nella lettura del file');
                setLoading(false);
            };
            reader.readAsText(file, 'UTF-8');
        } catch (err) {
            setError('Errore nel caricamento file: ' + err.message);
            setLoading(false);
        }
    };
    
    // Parse CSV text
    const parseCSV = (text) => {
        try {
            // Detect delimiter
            const firstLine = text.split('\n')[0];
            const detectedDelimiter = (firstLine.split(';').length > firstLine.split(',').length) ? ';' : ',';
            setDelimiter(detectedDelimiter);
            
            // Split lines
            const lines = text.split('\n').filter(line => line.trim());
            if (lines.length < 2) {
                setError('File CSV deve avere almeno header + 1 riga di dati');
                setLoading(false);
                return;
            }
            
            // Parse header
            const headers = lines[0].split(detectedDelimiter).map(h => h.trim()).filter(h => h);
            setCsvHeaders(headers);
            
            // Parse data rows
            const data = [];
            for (let i = 1; i < lines.length; i++) {
                const row = lines[i].split(detectedDelimiter).map(cell => cell.trim());
                if (row.some(cell => cell)) { // Skip empty rows
                    data.push(row);
                }
            }
            
            setCsvData(data);
            setStep('mapping');
            setLoading(false);
        } catch (err) {
            setError('Errore nel parsing CSV: ' + err.message);
            setLoading(false);
        }
    };
    
    // Update mapping
    const updateMapping = (csvHeader, dbField) => {
        setMapping(prev => ({
            ...prev,
            [csvHeader]: dbField
        }));
    };
    
    // Generate preview
    const generatePreview = () => {
        const previewData = [];
        const maxPreview = Math.min(5, csvData.length);
        
        for (let i = 0; i < maxPreview; i++) {
            const row = {};
            csvHeaders.forEach((header, index) => {
                const dbField = mapping[header];
                if (dbField && dbField !== 'ignore' && csvData[i] && csvData[i][index]) {
                    row[dbField] = csvData[i][index];
                }
            });
            if (Object.keys(row).length > 0) {
                previewData.push(row);
            }
        }
        
        return previewData;
    };
    
    // Handle import
    const handleImport = async () => {
        if (Object.values(mapping).filter(m => m && m !== 'ignore').length === 0) {
            setError('Seleziona almeno un campo da mappare');
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const api = getAuthenticatedApi();
            
            // Prepara dati CSV (header + righe) per invio
            const csvLines = [csvHeaders.join(delimiter)];
            csvData.forEach(row => {
                csvLines.push(row.join(delimiter));
            });
            const csvText = csvLines.join('\n');
            
            // Invia a API
            const formData = new FormData();
            formData.append('action', 'import');
            formData.append('csv_data', csvText);
            formData.append('mapping', JSON.stringify(mapping));
            formData.append('delimiter', delimiter);
            
            const response = await api.post('/rubrica_api.php', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            if (response.data.success) {
                setImportResult(response.data.data);
                setStep('complete');
            } else {
                setError(response.data.error || 'Errore nell\'import');
            }
        } catch (err) {
            console.error('Import error:', err);
            setError(err.response?.data?.error || 'Errore nell\'import: ' + err.message);
        } finally {
            setLoading(false);
        }
    };
    
    // Reset
    const handleReset = () => {
        setStep('upload');
        setCsvHeaders([]);
        setCsvData([]);
        setMapping({});
        setError(null);
        setImportResult(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    
    const previewData = step === 'preview' || step === 'complete' ? generatePreview() : [];

    const inputClass =
        'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-white/30';

    return (
        <div className="flex min-h-full w-full flex-col bg-gray-50 dark:bg-gray-900">
            <div className="shrink-0 bg-gradient-to-r from-[#FDFDFE] to-[#E5DFF1] dark:bg-gradient-to-r dark:from-gray-950 dark:via-gray-900 dark:to-gray-900">
                <div className="px-4 pb-4 pt-5 sm:px-6 sm:pb-5 sm:pt-6">
                    <div className="flex items-start gap-3 sm:gap-4">
                        <Link
                            to="/gym/members"
                            title={t('gym.navigation.members')}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200/80 bg-white/90 text-[#3a3a3a] shadow-sm transition hover:bg-white dark:border-white/15 dark:bg-white/10 dark:text-white dark:hover:bg-white/15 sm:h-12 sm:w-12"
                        >
                            <ArrowLeft size={20} className="shrink-0" aria-hidden />
                            <span className="sr-only">{t('gym.navigation.members')}</span>
                        </Link>
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/80 shadow-sm dark:bg-white/10 sm:h-12 sm:w-12">
                            <Upload className="text-blue-600 dark:text-gray-300" size={24} />
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                            <h1 className="text-2xl font-bold leading-tight text-[#3a3a3a] dark:font-open-sans dark:text-white sm:text-3xl">
                                Importa Contatti da CSV
                            </h1>
                            <p className="mt-1 text-sm text-gray-600 dark:font-open-sans dark:font-light dark:text-white/80 sm:text-base">
                                Carica un file CSV e mappa i campi per importare i contatti nella rubrica
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
                    {error && (
                        <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800/60 dark:bg-red-950/35">
                            <AlertCircle className="mt-0.5 size-5 shrink-0 text-red-600 dark:text-red-400" />
                            <p className="font-medium text-red-700 dark:text-red-200">{error}</p>
                        </div>
                    )}

                    {step === 'upload' && (
                        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-md dark:border-white/10 dark:bg-gray-900 dark:shadow-lg dark:shadow-black/35 sm:p-8">
                            <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/50 p-10 text-center transition-colors hover:border-blue-400 dark:border-white/20 dark:bg-black/10 dark:hover:border-white/35 sm:p-12">
                                <Upload className="mx-auto mb-4 text-gray-400 dark:text-white/45" size={48} />
                                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:font-open-sans dark:text-white">
                                    Carica File CSV
                                </h3>
                                <p className="mb-6 text-sm text-gray-600 dark:text-white/65">
                                    Seleziona un file CSV con i contatti da importare
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    id="csv-upload"
                                />
                                <label
                                    htmlFor="csv-upload"
                                    className="inline-block cursor-pointer rounded-xl bg-blue-600 px-6 py-3 text-white transition hover:bg-blue-700 dark:bg-gray-900 dark:font-open-sans dark:text-gray-300 dark:hover:brightness-110"
                                >
                                    {loading ? <LoadingSpinner size="small" /> : 'Seleziona File'}
                                </label>
                            </div>
                        </div>
                    )}
                
                    {step === 'mapping' && (
                        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-md dark:border-white/10 dark:bg-gray-900 dark:shadow-lg dark:shadow-black/35 sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-xl font-semibold text-gray-900 dark:font-open-sans dark:text-white">
                                <FileText size={24} className="text-blue-600 dark:text-gray-300" />
                                Mappa i Campi CSV
                            </h2>
                            <p className="mb-6 text-sm text-gray-600 dark:text-white/70">
                                Seleziona quale campo del CSV corrisponde a quale campo del database. Scegli &quot;Ignora&quot; per i campi che non vuoi importare.
                            </p>

                            <div className="max-h-[500px] space-y-3 overflow-y-auto pr-1">
                                {csvHeaders.map((header) => (
                                    <div
                                        key={header}
                                        className="flex flex-col items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 transition-colors hover:border-blue-300 dark:border-white/10 dark:bg-black/15 dark:hover:border-white/25 sm:flex-row sm:items-center sm:gap-4"
                                    >
                                        <div className="flex-1 text-sm font-medium text-gray-900 dark:text-white sm:text-base">{header}</div>
                                        <ChevronRight className="hidden shrink-0 text-gray-400 dark:text-white/40 sm:block" size={20} aria-hidden />
                                        <select
                                            value={mapping[header] || 'ignore'}
                                            onChange={(e) => updateMapping(header, e.target.value)}
                                            className={`${inputClass} sm:flex-1`}
                                        >
                                            <option value="ignore">-- Ignora --</option>
                                            {availableFields.map((field) => (
                                                <option key={field.value} value={field.value}>
                                                    {field.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end sm:gap-4">
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    className="rounded-xl border border-gray-300 bg-gray-100 px-6 py-2.5 font-medium text-gray-800 transition hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
                                >
                                    Annulla
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStep('preview')}
                                    className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-2.5 font-medium text-white transition hover:bg-blue-700 dark:bg-gray-900 dark:font-open-sans dark:text-gray-300 dark:hover:brightness-110"
                                >
                                    Anteprima
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        </div>
                    )}
                
                    {step === 'preview' && (
                        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-md dark:border-white/10 dark:bg-gray-900 dark:shadow-lg dark:shadow-black/35 sm:p-8">
                            <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:font-open-sans dark:text-white">Anteprima Import</h2>
                            <p className="mb-6 text-sm text-gray-600 dark:text-white/70">
                                Controlla i dati prima dell&apos;import. Verranno importati{' '}
                                <strong className="text-gray-900 dark:text-white">{csvData.length}</strong> contatti.
                            </p>

                            {previewData.length > 0 && (
                                <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 dark:border-white/10">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-white/10">
                                            <thead className="bg-gray-100 dark:bg-gray-800">
                                                <tr>
                                                    {Object.keys(previewData[0]).map((field) => (
                                                        <th
                                                            key={field}
                                                            className="border-r border-gray-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 last:border-r-0 dark:border-white/10 dark:text-white"
                                                        >
                                                            {availableFields.find((f) => f.value === field)?.label || field}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 bg-white dark:divide-white/10 dark:bg-gray-950">
                                                {previewData.map((row, index) => (
                                                    <tr key={index} className="transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.06]">
                                                        {Object.entries(row).map(([field, value]) => (
                                                            <td
                                                                key={field}
                                                                className="whitespace-nowrap border-r border-gray-200 px-4 py-3 text-sm text-gray-900 last:border-r-0 dark:border-white/10 dark:text-white"
                                                            >
                                                                {value || (
                                                                    <span className="italic text-gray-400 dark:text-white/45">-</span>
                                                                )}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {csvData.length > 5 && (
                                        <p className="border-t border-gray-200 py-3 text-center text-xs text-gray-500 dark:border-white/10 dark:text-white/55">
                                            Mostrati i primi 5 di {csvData.length} contatti
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end sm:gap-4">
                                <button
                                    type="button"
                                    onClick={() => setStep('mapping')}
                                    className="rounded-xl border border-gray-300 bg-gray-100 px-6 py-2.5 font-medium text-gray-800 transition hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
                                >
                                    Torna Indietro
                                </button>
                                <button
                                    type="button"
                                    onClick={handleImport}
                                    disabled={loading}
                                    className="flex items-center justify-center gap-2 rounded-xl bg-green-600 px-6 py-2.5 font-medium text-white transition hover:bg-green-700 disabled:opacity-50 dark:bg-emerald-700 dark:hover:bg-emerald-600"
                                >
                                    {loading ? (
                                        <LoadingSpinner size="small" />
                                    ) : (
                                        <>
                                            <Check size={20} />
                                            Importa {csvData.length} Contatti
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                
                    {step === 'complete' && importResult && (
                        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-md dark:border-white/10 dark:bg-gray-900 dark:shadow-lg dark:shadow-black/35 sm:p-8">
                            <div className="mb-6 text-center">
                                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-emerald-900/40">
                                    <Check className="h-8 w-8 text-green-600 dark:text-emerald-300" />
                                </div>
                                <h2 className="mb-2 text-2xl font-semibold text-gray-900 dark:font-open-sans dark:text-white">Import Completato!</h2>
                                <p className="text-gray-600 dark:text-white/75">Contatti importati con successo nella rubrica</p>
                            </div>

                            <div className="mb-6 rounded-xl border border-green-200 bg-green-50 p-6 dark:border-emerald-500/30 dark:bg-emerald-950/25">
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="font-semibold text-gray-900 dark:text-white">Importati:</span>
                                        <span className="text-lg font-bold text-green-700 dark:text-emerald-200">{importResult.imported}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="font-semibold text-gray-900 dark:text-white">Totali:</span>
                                        <span className="text-gray-700 dark:text-white/80">{importResult.total}</span>
                                    </div>
                                    {importResult.errors && importResult.errors.length > 0 && (
                                        <div className="mt-4 border-t border-green-200 pt-4 dark:border-emerald-500/25">
                                            <p className="mb-2 font-semibold text-red-600 dark:text-red-300">
                                                Errori ({importResult.errors.length}):
                                            </p>
                                            <ul className="max-h-32 list-inside list-disc space-y-1 overflow-y-auto text-sm">
                                                {importResult.errors.map((err, i) => (
                                                    <li key={i} className="text-red-700 dark:text-red-200">
                                                        {err}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-center">
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    className="rounded-xl bg-blue-600 px-6 py-2.5 font-medium text-white transition hover:bg-blue-700 dark:bg-gray-900 dark:font-open-sans dark:text-gray-300 dark:hover:brightness-110"
                                >
                                    Importa Altro File
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

